import { createRoute } from '@tanstack/react-router'

import { areaRoute } from '@/app/routes/area-layout'
import { lazyPage } from '@/lib/lazy-page'
import {
  normalizeDataGridUrlState,
  packageSourceEventsGridContract,
  packageSourcesGridContract,
} from '@/modules/data-grid'

export const PackageSources = lazyPage(() => import('./features/sources/package-sources.lazy'), 'PackageSources')
export const PackageSourceSettings = lazyPage(() => import('./features/sources/package-source-settings.lazy'), 'PackageSourceSettingsPage')
export const PackageSourceActivity = lazyPage(() => import('./features/sources/package-source-activity.lazy'), 'PackageSourceActivityPage')
export const PackageSourceConnection = lazyPage(() => import('./features/sources/package-source-connection.lazy'), 'PackageSourceConnectionPage')
export const PackageRolloutNewRoute = lazyPage(() => import('./features/rollouts/package-rollout-new.lazy'), 'PackageRolloutNewRoute')
export const PackageReleases = lazyPage(() => import('./features/releases/package-releases.lazy'), 'PackageReleases')
export const PackageReleaseHistoryRoute = lazyPage(() => import('./features/releases/package-release-history.lazy'), 'PackageReleaseHistoryRoute')
export const PackageReleaseUpload = lazyPage(() => import('./features/releases/package-release-upload.lazy'), 'PackageReleaseUpload')
export const PackageReleaseDetailRoute = lazyPage(() => import('./features/releases/package-release-detail.lazy'), 'PackageReleaseDetailRoute')

export type PackageSourcesSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodePackageSourcesSearch(
  search: Record<string, unknown>,
): PackageSourcesSearch {
  return normalizeDataGridUrlState(search, packageSourcesGridContract)
}

export const packageSourcesRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/sources',
  component: PackageSources,
  validateSearch: decodePackageSourcesSearch,
})

export const packageSourceNewRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/sources/new',
  component: lazyPage(() => import('./features/sources/package-source-settings.lazy'), 'PackageSourceNewPage'),
})

export const packageSourceSettingsRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/sources/$sourceId',
  component: PackageSourceSettings,
})

export function decodePackageSourceEventsSearch(
  search: Record<string, unknown>,
): PackageSourcesSearch {
  return normalizeDataGridUrlState(search, packageSourceEventsGridContract)
}

export const packageSourceActivityRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/sources/$sourceId/activity',
  component: PackageSourceActivity,
  validateSearch: decodePackageSourceEventsSearch,
})

export const packageSourceConnectionRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/sources/$sourceId/connection',
  component: PackageSourceConnection,
})

export const packageRolloutNewRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/releases/$releaseId/rollouts/new',
  component: PackageRolloutNewRoute,
})

export const packageReleasesRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/releases',
  component: PackageReleases,
})

export const packageReleaseHistoryRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/history/$packageType/$packageId',
  component: PackageReleaseHistoryRoute,
})

export const packageReleaseUploadRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/releases/upload',
  component: PackageReleaseUpload,
})

export const packageReleaseDetailRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/releases/$releaseId',
  component: PackageReleaseDetailRoute,
})

export const PackageRolloutTrackingRoute = lazyPage(() => import('./features/rollouts/package-rollout-tracking.lazy'), 'PackageRolloutTrackingRoute')

export const packageRolloutTrackingRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'packages/rollouts/$rolloutId',
  component: PackageRolloutTrackingRoute,
})
